import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiFile, saveBlob } from '../api';
import { useAuth } from '../auth';
import { ErrorBox, Field, Loading, Success, fmtDate } from '../components/ui';

interface Series {
  prefix: string;
  digits: number;
  nextNumber: number;
  nextLabel: string;
}
interface Batch {
  id: string;
  firstLabel: string;
  lastLabel: string;
  quantity: number;
  note: string | null;
  createdAt: string;
  createdByName: string;
  usedCount: number;
  printCount: number;
}

export function LabelsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const qc = useQueryClient();
  const series = useQuery({ queryKey: ['labelSeries'], queryFn: () => api<{ series: Series }>('/labels/series').then((r) => r.series) });
  const batches = useQuery({ queryKey: ['labelBatches'], queryFn: () => api<{ batches: Batch[] }>('/labels/batches').then((r) => r.batches) });
  const [qty, setQty] = useState('100');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['labelSeries'] });
    qc.invalidateQueries({ queryKey: ['labelBatches'] });
  };

  const run = async (key: string, fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(key);
    setError(null);
    setMsg(null);
    try {
      await fn();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  };

  const print = (b: Batch) =>
    run(`print-${b.id}`, async () => {
      let reason: string | undefined;
      if (b.printCount > 0) {
        reason = prompt(
          `Batch ${b.firstLabel}–${b.lastLabel} was already printed. Printing again creates DUPLICATE physical labels; destroy any spares. Reason for reprint:`,
        )?.trim();
        if (!reason) return;
      }
      const pdf = await apiFile(`/labels/batches/${b.id}/print`, reason ? { reason } : {});
      saveBlob(pdf, `labels-${b.firstLabel}-${b.lastLabel}.pdf`);
      setMsg(`Downloaded ${b.quantity} labels (${b.firstLabel}–${b.lastLabel}). Open the PDF and print at actual size.`);
      refresh();
    });

  const generate = (e: React.FormEvent) => {
    e.preventDefault();
    const n = Number(qty);
    if (!Number.isInteger(n) || n < 1 || n > 5000) return setError(new Error('Enter a quantity from 1 to 5000'));
    run('generate', async () => {
      const r = await api<{ batch: { id: string; firstLabel: string; lastLabel: string } }>('/labels/batches', { body: { quantity: n, note: note || undefined } });
      setMsg(`Issued ${n} new label numbers: ${r.batch.firstLabel} to ${r.batch.lastLabel}. Press Print to download them.`);
      setNote('');
      refresh();
    });
  };

  return (
    <div>
      <h1>Lot labels</h1>
      <p className="muted">
        Each label shows only the lot number and its barcode (Code 128) on a 1.5″ × 1″ label. Numbers are issued once and never reused. Only issued labels can be assigned to lots.
      </p>
      <ErrorBox error={error} />
      {msg && <Success>{msg}</Success>}

      {isAdmin && (
        <div className="grid2">
          <form className="card" onSubmit={generate}>
            <h2>Issue new labels</h2>
            {series.data && (
              <p>
                Next number: <strong className="mono">{series.data.nextLabel}</strong>
              </p>
            )}
            <div className="grid2">
              <Field label="How many">
                <input value={qty} onChange={(e) => setQty(e.target.value)} inputMode="numeric" />
              </Field>
              <Field label="Note (optional)">
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. roll for truck 14" />
              </Field>
            </div>
            <button className="btn btn-primary" disabled={!!busy}>
              {busy === 'generate' ? 'Issuing…' : 'Issue numbers'}
            </button>
          </form>
          <section className="card">
            <h2>Printing</h2>
            <ol className="small">
              <li>In the printer settings, set the paper size to <strong>1.5 × 1 in (38 × 25 mm)</strong>.</li>
              <li>
                Print at <strong>Actual size / 100%</strong> (not "Fit to page").
              </li>
              <li>
                First print a <a href="/api/labels/test.pdf" target="_blank" rel="noreferrer">test label</a> (TEST1234, never a real number) and scan it with your scanner.
              </li>
            </ol>
            <SeriesForm series={series.data} onSaved={refresh} />
          </section>
        </div>
      )}

      <section className="card">
        <h2>Issued batches</h2>
        {batches.isLoading && <Loading />}
        <ErrorBox error={batches.error} />
        {batches.data?.length === 0 && <p className="muted">No labels issued yet.</p>}
        {!!batches.data?.length && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Range</th>
                  <th>Qty</th>
                  <th>Used on lots</th>
                  <th>Printed</th>
                  <th>Note</th>
                  <th>Issued</th>
                  {isAdmin && <th></th>}
                </tr>
              </thead>
              <tbody>
                {batches.data.map((b) => (
                  <tr key={b.id}>
                    <td className="mono">
                      {b.firstLabel} – {b.lastLabel}
                    </td>
                    <td>{b.quantity}</td>
                    <td>{b.usedCount}</td>
                    <td>{b.printCount === 0 ? <span className="badge">not yet</span> : `${b.printCount}×`}</td>
                    <td>{b.note}</td>
                    <td className="small">
                      {fmtDate(b.createdAt)} · {b.createdByName}
                    </td>
                    {isAdmin && (
                      <td>
                        <button className={`btn btn-small ${b.printCount === 0 ? 'btn-primary' : ''}`} disabled={!!busy} onClick={() => print(b)}>
                          {busy === `print-${b.id}` ? 'Preparing…' : b.printCount === 0 ? 'Print' : 'Reprint…'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {isAdmin && <ReprintForm />}
    </div>
  );
}

function SeriesForm({ series, onSaved }: { series?: Series; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const [prefix, setPrefix] = useState('');
  const [digits, setDigits] = useState('');
  const [error, setError] = useState<unknown>(null);
  if (!series) return null;
  if (!open)
    return (
      <p className="small muted">
        Format: prefix <span className="mono">"{series.prefix}"</span> + {series.digits} digits.{' '}
        <button className="btn btn-small btn-ghost" onClick={() => (setOpen(true), setPrefix(series.prefix), setDigits(String(series.digits)))}>
          Change
        </button>
      </p>
    );
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        try {
          await api('/labels/series', { method: 'PUT', body: { prefix, digits: Number(digits) } });
          setOpen(false);
          onSaved();
        } catch (err) {
          setError(err);
        }
      }}
    >
      <div className="grid2">
        <Field label="Prefix (letters, optional)" hint="A letter keeps lot labels distinct from item numbers and UPCs">
          <input value={prefix} onChange={(e) => setPrefix(e.target.value.toUpperCase())} maxLength={4} />
        </Field>
        <Field label="Digits">
          <input value={digits} onChange={(e) => setDigits(e.target.value)} inputMode="numeric" />
        </Field>
      </div>
      <ErrorBox error={error} />
      <button className="btn btn-small btn-primary">Save format</button>{' '}
      <button type="button" className="btn btn-small btn-ghost" onClick={() => setOpen(false)}>
        Cancel
      </button>
    </form>
  );
}

function ReprintForm() {
  const [labels, setLabels] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <form
      className="card narrow"
      onSubmit={async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        setError(null);
        try {
          const list = labels.split(/[\s,]+/).filter(Boolean);
          saveBlob(await apiFile('/labels/reprint', { labels: list, reason }), `reprint-${list[0]}.pdf`);
          setLabels('');
          setReason('');
        } catch (err) {
          setError(err);
        } finally {
          setBusy(false);
        }
      }}
    >
      <h2>Reprint specific labels</h2>
      <p className="small muted">For a damaged or lost label. Only reprint a number that is not already on another item. Every reprint is recorded.</p>
      <Field label="Label numbers (separated by spaces or commas)">
        <input value={labels} onChange={(e) => setLabels(e.target.value)} className="mono" required />
      </Field>
      <Field label="Reason (required)">
        <input value={reason} onChange={(e) => setReason(e.target.value)} required minLength={3} />
      </Field>
      <ErrorBox error={error} />
      <button className="btn" disabled={busy}>
        {busy ? 'Preparing…' : 'Download reprint PDF'}
      </button>
    </form>
  );
}
